'use client';

import { Fragment, useCallback, useContext, useMemo } from 'react';

import type { PartiallyRequiredType } from '../../../types/partially-required';
import { Highlighter } from '../../highlighter';
import { InputChip } from '../../input-chip';
import { ComboboxAdornment } from '../combobox-adornment';
import { ComboboxChipsOverflow } from '../combobox-chips-overflow';
import { ComboboxClear } from '../combobox-clear';
import { ComboboxEmptyState } from '../combobox-empty-state';
import { ComboboxGroup } from '../combobox-group';
import { ComboboxHeading } from '../combobox-heading';
import { ComboboxInput } from '../combobox-input';
import { ComboboxInputGroup } from '../combobox-input-group';
import { ComboboxLoadingState } from '../combobox-loading-state';
import { ComboboxOption } from '../combobox-option';
import { ComboboxOptionLabel } from '../combobox-option-label';
import { ComboboxOptionMedia } from '../combobox-option-media';
import { ComboboxOptionMeta } from '../combobox-option-meta';
import { ComboboxRootContext } from '../context/combobox-root.context';
import type { ComboboxContentPropsInterface } from '../types/combobox-content.types';
import type { ComboboxTriggerPropsInterface } from '../types/combobox-trigger.types';

export const useComboboxContentRenderProps = (props: ComboboxContentPropsInterface) => {
  const { showOptionControl, combobox } = useContext(ComboboxRootContext);

  const {
    renderEmptyState,
    renderGroup,
    renderHeading,
    renderLoadingState,
    renderOption,
    renderOptionLabel,
    renderOptionMedia,
    renderOptionMeta,
    ...rest
  } = props;

  const defaultRenderEmptyState: typeof renderEmptyState = (renderProps) => (
    <ComboboxEmptyState {...renderProps} />
  );

  const defaultRenderGroup: typeof renderGroup = (renderProps) => {
    const {
      group,
      highlightPattern,
      renderHeading,
      renderOption,
      renderOptionLabel,
      renderOptionMedia,
      renderOptionMeta,
      key,
      ...renderPropsRest
    } = renderProps;

    const renderContent = (
      <Fragment>
        {renderHeading({ level: 4, heading: group.heading })}
        {group.options.map((option, optionIndex) =>
          renderOption({
            key: optionIndex,
            option,
            highlightPattern,
            renderOptionLabel,
            renderOptionMedia,
            renderOptionMeta,
          }),
        )}
      </Fragment>
    );

    return (
      <ComboboxGroup key={key} {...renderPropsRest}>
        {renderContent}
      </ComboboxGroup>
    );
  };

  const defaultRenderHeading: typeof renderHeading = (renderProps) => {
    const { heading, ...renderPropsRest } = renderProps;
    const visuallyHidden = typeof heading === 'object' && heading.visuallyHidden;
    const renderContent =
      typeof heading === 'object' ? (
        <Fragment>
          {heading.icon}
          {heading.label}
        </Fragment>
      ) : (
        heading
      );

    return (
      <ComboboxHeading visuallyHidden={visuallyHidden} {...renderPropsRest}>
        {renderContent}
      </ComboboxHeading>
    );
  };

  const defaultRenderLoadingState: typeof renderLoadingState = (renderProps) => (
    <ComboboxLoadingState {...renderProps} />
  );

  const defaultRenderOption = useCallback<
    NonNullable<ComboboxContentPropsInterface['renderOption']>
  >(
    (renderProps) => {
      const {
        option,
        renderOptionLabel,
        renderOptionMedia,
        renderOptionMeta,
        highlightPattern,
        key,
        ...renderPropsRest
      } = renderProps;

      const optionLabel = typeof option === 'object' ? option.label : option;
      const optionMeta = typeof option === 'object' ? option.meta || '' : '';

      const renderContent =
        typeof option === 'object' ? (
          <Fragment>
            {option.icon || showOptionControl || combobox.isMultiSelect
              ? renderOptionMedia({ children: option.icon })
              : null}
            {renderOptionLabel({ children: optionLabel, highlightPattern })}
            {option.meta ? renderOptionMeta({ children: optionMeta, highlightPattern }) : null}
          </Fragment>
        ) : (
          <Fragment>
            {showOptionControl || combobox.isMultiSelect ? renderOptionMedia({}) : null}
            {renderOptionLabel({ children: optionLabel, highlightPattern })}
          </Fragment>
        );

      return (
        <ComboboxOption key={key} option={option} {...renderPropsRest}>
          {renderContent}
        </ComboboxOption>
      );
    },
    [combobox, showOptionControl],
  );

  const defaultRenderOptionLabel: typeof renderOptionLabel = (renderProps) => {
    const { children, highlightPattern, ...renderPropsRest } = renderProps;
    const label =
      highlightPattern && typeof children === 'string' ? (
        <Highlighter pattern={highlightPattern} text={children} />
      ) : (
        children
      );
    return <ComboboxOptionLabel {...renderPropsRest}>{label}</ComboboxOptionLabel>;
  };

  const defaultRenderOptionMedia: typeof renderOptionMedia = (renderProps) => (
    <ComboboxOptionMedia {...renderProps} />
  );

  const defaultRenderOptionMeta: typeof renderOptionMeta = (renderProps) => {
    const { children, highlightPattern, ...renderPropsRest } = renderProps;
    const meta =
      highlightPattern && typeof children === 'string' ? (
        <Highlighter pattern={highlightPattern} text={children} />
      ) : (
        children
      );
    return <ComboboxOptionMeta {...renderPropsRest}>{meta}</ComboboxOptionMeta>;
  };

  return useMemo<
    PartiallyRequiredType<
      ComboboxContentPropsInterface,
      | 'renderEmptyState'
      | 'renderGroup'
      | 'renderHeading'
      | 'renderLoadingState'
      | 'renderOption'
      | 'renderOptionLabel'
      | 'renderOptionMedia'
      | 'renderOptionMeta'
    >
  >(
    () => ({
      renderEmptyState: renderEmptyState ?? defaultRenderEmptyState,
      renderGroup: renderGroup ?? defaultRenderGroup,
      renderHeading: renderHeading ?? defaultRenderHeading,
      renderLoadingState: renderLoadingState ?? defaultRenderLoadingState,
      renderOption: renderOption ?? defaultRenderOption,
      renderOptionLabel: renderOptionLabel ?? defaultRenderOptionLabel,
      renderOptionMedia: renderOptionMedia ?? defaultRenderOptionMedia,
      renderOptionMeta: renderOptionMeta ?? defaultRenderOptionMeta,
      ...rest,
    }),
    // No need to observe rest since it won't affect any render props
    // eslint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps)
    [
      defaultRenderOption,
      renderEmptyState,
      renderGroup,
      renderHeading,
      renderLoadingState,
      renderOption,
      renderOptionLabel,
      renderOptionMedia,
      renderOptionMeta,
    ],
  );
};

export const useComboboxTriggerRenderProps = (props: ComboboxTriggerPropsInterface) => {
  const { disabled } = useContext(ComboboxRootContext);

  const {
    renderAdornment,
    renderChip,
    renderChipsOverflow,
    renderClear,
    renderInput,
    renderInputGroup,
    ...rest
  } = props;

  const defaultRenderAdornment: typeof renderAdornment = (renderProps) => (
    <ComboboxAdornment {...renderProps} />
  );

  const defaultRenderChip = useCallback<NonNullable<ComboboxTriggerPropsInterface['renderChip']>>(
    (renderProps) => {
      const { children, onClick, key, isLast: _isLast, ...renderPropsRest } = renderProps;

      return (
        <InputChip.Root key={key} {...renderPropsRest}>
          <InputChip.Label>{children}</InputChip.Label>
          {disabled ? null : <InputChip.Close onClick={onClick} />}
        </InputChip.Root>
      );
    },
    [disabled],
  );

  const defaultRenderChipsOverflow: typeof renderChipsOverflow = (renderProps) => (
    <ComboboxChipsOverflow {...renderProps} />
  );

  const defaultRenderClear: typeof renderClear = (renderProps) => (
    <ComboboxClear {...renderProps} />
  );

  const defaultRenderInput: typeof renderInput = (renderProps) => (
    <ComboboxInput {...renderProps} />
  );

  const defaultRenderInputGroup: typeof renderInputGroup = (renderProps) => (
    <ComboboxInputGroup {...renderProps} />
  );

  return useMemo<
    PartiallyRequiredType<
      ComboboxTriggerPropsInterface,
      | 'renderAdornment'
      | 'renderChip'
      | 'renderChipsOverflow'
      | 'renderClear'
      | 'renderInput'
      | 'renderInputGroup'
    >
  >(
    () => ({
      renderAdornment: renderAdornment ?? defaultRenderAdornment,
      renderChip: renderChip ?? defaultRenderChip,
      renderChipsOverflow: renderChipsOverflow ?? defaultRenderChipsOverflow,
      renderClear: renderClear ?? defaultRenderClear,
      renderInput: renderInput ?? defaultRenderInput,
      renderInputGroup: renderInputGroup ?? defaultRenderInputGroup,
      ...rest,
    }),
    // No need to observe rest since it won't affect any render props
    // eslint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps)
    [
      defaultRenderChip,
      renderAdornment,
      renderChip,
      renderChipsOverflow,
      renderClear,
      renderInput,
      renderInputGroup,
    ],
  );
};
